import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { X, Filter } from 'lucide-react';
import { DayOfWeek } from '@/models/Schedule';
import { WorkoutType } from '@/models/WorkoutType';
import { useTranslation } from 'react-i18next';

interface ScheduleFiltersProps {
  selectedDay: DayOfWeek | 'all';
  selectedWorkoutType: string | 'all';
  workoutTypes: WorkoutType[];
  onDayChange: (day: DayOfWeek | 'all') => void;
  onWorkoutTypeChange: (workoutTypeId: string | 'all') => void;
  onClearFilters: () => void;
}

const DAYS: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  selectedDay,
  selectedWorkoutType,
  workoutTypes,
  onDayChange,
  onWorkoutTypeChange,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  const hasActiveFilters =
    selectedDay !== 'all' || selectedWorkoutType !== 'all';

  const getDayTranslation = (day: DayOfWeek) => {
    return t(`schedule.days.${day}`);
  };

  return (
    <Card className="mb-4">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="filters" className="border-none">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">{t('common.filters')}</span>
              {hasActiveFilters && (
                <div className="h-2 w-2 bg-blue-500 rounded-full" />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="flex-1 min-w-0">
                  <Select value={selectedDay} onValueChange={onDayChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t('schedule.form.selectDays')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.allDays')}</SelectItem>
                      {DAYS.map((day) => (
                        <SelectItem key={day} value={day}>
                          {getDayTranslation(day)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-0">
                  <Select
                    value={selectedWorkoutType}
                    onValueChange={onWorkoutTypeChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('schedule.workoutType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t('common.allWorkingTypes')}
                      </SelectItem>
                      {workoutTypes.map((workoutType) => (
                        <SelectItem key={workoutType.id} value={workoutType.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: workoutType.color }}
                            />
                            {workoutType.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClearFilters}
                  className="flex items-center gap-2 shrink-0"
                >
                  <X className="h-4 w-4" />
                  {t('common.clear')}
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default ScheduleFilters;
